import { Clock, Thermometer, Package, User, Edit, Trash2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LazyImage } from "../common/LazyImage";

const STOOL_COLOR_IMAGES = {
  'Kuning Mustard': '/assets/stool/kuning_mustard.png',
  'Coklat Mentega': '/assets/stool/coklat_mentega.png',
  'Hijau': '/assets/stool/hijau.png',
  'Kelabu Pucat/Putih': '/assets/stool/kelabu.png',
  'Merah': '/assets/stool/merah.png',
  'Hijau Kehitaman': '/assets/stool/hijau_kehitaman.png'
};

export default function DailyLogCard({ log, child, onEdit, onDelete, onViewImage, onDuplicate, showActions = true, isHighlighted = false, user = null }) {
  const getAttendanceColor = (attendance) => {
    if (attendance === "Hadir") return "bg-green-100 text-green-700";
    if (attendance === "Tidak Hadir") return "bg-red-100 text-red-700";
    return "bg-yellow-100 text-yellow-700";
  };

  const getMoodEmoji = (mood) => {
    // Match mood values from DailyLogForm.js
    const moods = { 
      sihat: "🥰", 
      sakit: "🤒", 
      mengantuk: "😴", 
      sedih: "😢",
      // Legacy support
      happy: "😊", 
      sad: "😢", 
      neutral: "😐", 
      tired: "😴", 
      excited: "🤩", 
      angry: "😠" 
    };
    return moods[mood] || "🥰";
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden hover:shadow-xl transition-all ${isHighlighted ? 'ring-4 ring-pink-400 ring-offset-2 animate-pulse' : ''}`}>
      {/* Header with photo */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-4">
        <div className="flex items-center gap-3">
          {child?.photo ? (
            <LazyImage 
              src={child.photo} 
              alt={child?.name} 
              className="w-12 h-12 rounded-full border-2 border-white"
              fallbackColor="bg-white/20"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-lg font-bold">
              {(log.child_name || child?.name)?.charAt(0) || '?'}
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-white font-semibold">{log.child_name || child?.name || "Unknown"}</h3>
            <p className="text-blue-100 text-sm">{child?.class_name || "-"}</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getAttendanceColor(log.attendance)}`}>
            {log.attendance}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Status Saringan Section */}
        {log.status_saringan && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
            <p className="text-xs font-semibold text-purple-700 mb-2">📋 Status Saringan</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {/* Waktu Tiba */}
              {log.status_saringan.waktu_tiba?.hour && (
                <div className="bg-white p-2 rounded">
                  <span className="text-gray-500">🕐 Tiba:</span>
                  <span className="ml-1 font-medium">
                    {log.status_saringan.waktu_tiba.hour}:{log.status_saringan.waktu_tiba.minute || '00'} {log.status_saringan.waktu_tiba.period}
                  </span>
                </div>
              )}
              {/* Penghantar */}
              {log.status_saringan.penghantar && (
                <div className="bg-white p-2 rounded">
                  <span className="text-gray-500">👤 Penghantar:</span>
                  <span className="ml-1 font-medium">
                    {log.status_saringan.penghantar.ibu_bapa && "Ibu Bapa"}
                    {log.status_saringan.penghantar.keluarga && "Keluarga"}
                    {log.status_saringan.penghantar.penjaga && "Penjaga"}
                    {log.status_saringan.penghantar.perkhidmatan && "Perkhidmatan"}
                    {log.status_saringan.penghantar.lain_lain && log.status_saringan.penghantar.lain_lain_text}
                  </span>
                </div>
              )}
              {/* Status Kesihatan */}
              {log.status_saringan.status_kesihatan?.sihat && (
                <div className="bg-white p-2 rounded">
                  <span className="text-gray-500">💚 Kesihatan:</span>
                  <span className={`ml-1 font-medium ${log.status_saringan.status_kesihatan.sihat === 'sihat' ? 'text-green-600' : 'text-orange-600'}`}>
                    {log.status_saringan.status_kesihatan.sihat === 'sihat' ? '✓ Sihat' : '⚠ Kurang Sihat'}
                  </span>
                  {log.status_saringan.status_kesihatan.suhu_badan && (
                    <span className="ml-1">({log.status_saringan.status_kesihatan.suhu_badan}°C)</span>
                  )}
                </div>
              )}
              {/* Sudah Mandi */}
              {log.status_saringan.sudah_mandi && (
                <div className="bg-white p-2 rounded">
                  <span className="text-gray-500">🛁 Mandi:</span>
                  <span className={`ml-1 font-medium ${log.status_saringan.sudah_mandi === 'ya' ? 'text-green-600' : 'text-red-600'}`}>
                    {log.status_saringan.sudah_mandi === 'ya' ? '✓ Sudah' : '✗ Belum'}
                  </span>
                </div>
              )}
            </div>
            {/* Alatan Dalam Beg */}
            {log.status_saringan.alatan_dalam_beg && (
              <div className="mt-2 bg-white p-2 rounded">
                <span className="text-gray-500 text-xs">🎒 Alatan:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {log.status_saringan.alatan_dalam_beg.botol_susu && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Botol Susu: {log.status_saringan.alatan_dalam_beg.botol_susu}</span>
                  )}
                  {log.status_saringan.alatan_dalam_beg.lampin && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Lampin: {log.status_saringan.alatan_dalam_beg.lampin}</span>
                  )}
                  {log.status_saringan.alatan_dalam_beg.botol_air && (
                    <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs">Botol Air: {log.status_saringan.alatan_dalam_beg.botol_air}</span>
                  )}
                  {log.status_saringan.alatan_dalam_beg.pakaian_bersih && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Pakaian: {log.status_saringan.alatan_dalam_beg.pakaian_bersih}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Detail Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* SUSU */}
          {log.susu && (log.susu.times?.length > 0 || log.susu.quantity_oz || log.susu.frequency) && (
            <div className="bg-pink-50 rounded-lg p-3 border border-pink-200">
              <p className="text-xs font-semibold text-pink-700 mb-1">🍼 Susu</p>
              <div className="text-xs text-gray-600 space-y-1">
                {log.susu.times?.length > 0 && (
                  <div>
                    <span className="font-medium">Masa: </span>
                    {log.susu.times.map(t => (
                      <span key={t} className="inline-block bg-pink-100 px-1.5 py-0.5 rounded mr-1 mb-1">{t}</span>
                    ))}
                  </div>
                )}
                {log.susu.quantity_oz && <p>Kuantiti: {log.susu.quantity_oz} oz</p>}
                {log.susu.frequency && <p>Kekerapan: {log.susu.frequency}x</p>}
                {log.susu.notes && <p className="italic mt-1">&quot;{log.susu.notes}&quot;</p>}
              </div>
            </div>
          )}

          {/* Lampin/Tandas */}
          {log.diaper_change && (log.diaper_change.times?.length > 0 || log.diaper_change.frequency || log.diaper_change.photo_url) && (
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <p className="text-xs font-semibold text-yellow-700 mb-1">🚼 Lampin/Tandas</p>
              <div className="text-xs text-gray-600 space-y-1">
                {log.diaper_change.times?.length > 0 && (
                  <div>
                    <span className="font-medium">Masa: </span>
                    {log.diaper_change.times.map(t => (
                      <span key={t} className="inline-block bg-yellow-100 px-1.5 py-0.5 rounded mr-1 mb-1">{t}</span>
                    ))}
                  </div>
                )}
                {log.diaper_change.frequency && <p>Kekerapan: {log.diaper_change.frequency}x</p>}
                {log.diaper_change.stool_type && <p>Najis: {log.diaper_change.stool_type}</p>}
                {log.diaper_change.stool_color && (
                  <div className="flex items-center gap-1.5">
                    <span>Warna:</span>
                    {STOOL_COLOR_IMAGES[log.diaper_change.stool_color] && (
                      <img src={STOOL_COLOR_IMAGES[log.diaper_change.stool_color]} alt={log.diaper_change.stool_color} className="w-6 h-6 object-contain" />
                    )}
                    <span className="font-medium">{log.diaper_change.stool_color}</span>
                  </div>
                )}
                {log.diaper_change.notes && <p>Nota: {log.diaper_change.notes}</p>}
              </div>
              {/* Gambar Najis */}
              {log.diaper_change.photo_url && (
                <div className="mt-2">
                  <p className="text-xs text-yellow-600 mb-1">📷 Gambar:</p>
                  <LazyImage 
                    src={log.diaper_change.photo_url} 
                    alt="Gambar najis" 
                    className="w-16 h-16 rounded cursor-pointer hover:opacity-80 border-2 border-yellow-300"
                    onClick={() => onViewImage(log.diaper_change.photo_url)}
                    fallbackColor="bg-yellow-100"
                  />
                </div>
              )}
            </div>
          )}

          {/* Gosok Gigi */}
          {log.brush_teeth && (log.brush_teeth.morning || log.brush_teeth.afternoon || log.brush_teeth.evening) && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 mb-1">🦷 Gosok Gigi</p>
              <div className="text-xs text-gray-600 flex gap-2">
                {log.brush_teeth.morning && <span className="bg-blue-100 px-2 py-0.5 rounded">Pagi ✓</span>}
                {log.brush_teeth.afternoon && <span className="bg-blue-100 px-2 py-0.5 rounded">Petang ✓</span>}
                {log.brush_teeth.evening && <span className="bg-blue-100 px-2 py-0.5 rounded">Malam ✓</span>}
              </div>
            </div>
          )}

          {/* Mandi */}
          {log.bath && (log.bath.morning || log.bath.evening) && (
            <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
              <p className="text-xs font-semibold text-cyan-700 mb-1">🛁 Mandi</p>
              <div className="text-xs text-gray-600 flex gap-2">
                {log.bath.morning && <span className="bg-cyan-100 px-2 py-0.5 rounded">Pagi ✓</span>}
                {log.bath.evening && <span className="bg-cyan-100 px-2 py-0.5 rounded">Petang ✓</span>}
              </div>
            </div>
          )}

          {/* Tidur */}
          {log.sleep && (log.sleep.count || log.sleep.until_time) && (
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
              <p className="text-xs font-semibold text-indigo-700 mb-1">😴 Tidur</p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-2">
                {log.sleep.count && <span className="bg-indigo-100 px-2 py-0.5 rounded">{log.sleep.count} kali</span>}
                {(parseInt(log.sleep.count) || 0) >= 2 && Array.isArray(log.sleep.sessions) && log.sleep.sessions.length > 0 ? (
                  log.sleep.sessions.map((s, i) => (
                    <span key={i} className="bg-indigo-100 px-2 py-0.5 rounded">
                      ({i + 1}) {s?.start_time || '-'} - {s?.until_time || '-'}
                    </span>
                  ))
                ) : (
                  <>
                    {log.sleep.start_time && <span className="bg-indigo-100 px-2 py-0.5 rounded">Mula {log.sleep.start_time}</span>}
                    {log.sleep.until_time && <span className="bg-indigo-100 px-2 py-0.5 rounded">Sehingga {log.sleep.until_time}</span>}
                  </>
                )}
              </div>
              {log.sleep.notes && <p className="text-xs text-gray-500 mt-1">{log.sleep.notes}</p>}
            </div>
          )}
        </div>

        {/* Mood & Notes */}
        {(log.mood || log.notes) && (
          <div className="flex items-start gap-3 pt-2 border-t border-gray-100">
            {log.mood && (
              <div className="text-2xl" title={log.mood}>{getMoodEmoji(log.mood)}</div>
            )}
            {log.notes && (
              <p className="text-sm text-gray-600 flex-1">&quot;{log.notes}&quot;</p>
            )}
          </div>
        )}

        {/* Health Notes & Photo */}
        {(log.health_notes || log.health_photo_url) && (
          <div className="bg-red-50 rounded-lg p-3 border border-red-200">
            <p className="text-xs font-semibold text-red-700 mb-1">🏥 Nota Kesihatan</p>
            {log.health_notes && <p className="text-xs text-gray-600">{log.health_notes}</p>}
            {log.health_photo_url && (
              <LazyImage 
                src={log.health_photo_url} 
                alt="Health" 
                className="mt-2 w-16 h-16 rounded cursor-pointer hover:opacity-80"
                onClick={() => onViewImage(log.health_photo_url)}
                fallbackColor="bg-red-100"
              />
            )}
          </div>
        )}

        {/* Injury/Kecederaan Section */}
        {log.injury && (log.injury.description || log.injury.location) && (
          <div className="bg-orange-50 rounded-lg p-3 border-2 border-orange-300">
            <p className="text-xs font-bold text-orange-700 mb-2 flex items-center gap-1">
              🩹 Kecederaan / Penyakit
            </p>
            <div className="space-y-2">
              {/* Main Info */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {log.injury.description && (
                  <div className="col-span-2 bg-white p-2 rounded border border-orange-200">
                    <span className="text-gray-500 block mb-0.5">Jenis:</span>
                    <span className="font-semibold text-orange-800">{log.injury.description}</span>
                  </div>
                )}
                {log.injury.location && (
                  <div className="bg-white p-2 rounded border border-orange-200">
                    <span className="text-gray-500 block mb-0.5">Lokasi:</span>
                    <span className="font-semibold text-gray-700">{log.injury.location}</span>
                  </div>
                )}
                {log.injury.time_detected && (
                  <div className="bg-white p-2 rounded border border-orange-200">
                    <span className="text-gray-500 block mb-0.5">Waktu Dikesan:</span>
                    <span className="font-semibold text-gray-700">{log.injury.time_detected}</span>
                  </div>
                )}
                {log.injury.origin && (
                  <div className="bg-white p-2 rounded border border-orange-200 col-span-2">
                    <span className="text-gray-500 block mb-0.5">Punca Berlaku:</span>
                    <span className={`font-semibold ${log.injury.origin === 'Dari rumah' ? 'text-blue-600' : log.injury.origin === 'Di taska' ? 'text-orange-600' : 'text-gray-600'}`}>
                      {log.injury.origin}
                    </span>
                  </div>
                )}
              </div>
              {/* Notes */}
              {log.injury.notes && (
                <p className="text-xs text-gray-600 italic bg-white p-2 rounded border border-orange-200">
                  "{log.injury.notes}"
                </p>
              )}
            </div>
          </div>
        )}

        {/* Ubat Diberi Section */}
        {log.ubat_diberi && Object.keys(log.ubat_diberi).some(key => log.ubat_diberi[key]?.selected) && (
          <div className="bg-red-50 rounded-lg p-3 border-2 border-red-300">
            <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1">
              💊 Ubat Diberi
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'demam', label: 'Demam', emoji: '🤒' },
                { id: 'batuk', label: 'Batuk', emoji: '😷' },
                { id: 'selsema', label: 'Selsema', emoji: '🤧' },
                { id: 'antibiotik', label: 'Antibiotik', emoji: '💉' },
                { id: 'ubat_titik', label: 'Ubat Titik', emoji: '💧' },
                { id: 'ubat_sapu', label: 'Ubat Sapu', emoji: '🧴' }
              ].map(ubat => {
                const ubatData = log.ubat_diberi[ubat.id];
                if (!ubatData?.selected) return null;
                return (
                  <div key={ubat.id} className="bg-white p-2 rounded border border-red-200 text-xs">
                    <div className="font-semibold text-red-700">{ubat.emoji} {ubat.label}</div>
                    {ubatData.hour && (
                      <div className="text-gray-600">
                        🕐 {ubatData.hour}:{ubatData.minute || '00'} {ubatData.period || 'AM'}
                      </div>
                    )}
                    {ubatData.notes && (
                      <div className="text-gray-500 italic mt-1">"{ubatData.notes}"</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {showActions && (
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            {onDuplicate && (
              <Button size="sm" variant="outline" onClick={() => onDuplicate(log)} className="text-green-600 hover:bg-green-50">
                <Copy className="w-4 h-4 mr-1" /> Duplicate
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onEdit(log)} className="text-blue-600">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(log.id)} className="text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" /> Padam
            </Button>
          </div>
        )}
        
        {/* Teacher Name • Timestamp - hidden for ibu_bapa */}
        {user && user.role !== 'ibu_bapa' && (log.teacher_name || log.created_at) && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              {log.teacher_name && <span>{log.teacher_name}</span>}
              {log.teacher_name && log.created_at && <span> • </span>}
              {log.created_at && (
                <span>{new Date(log.created_at).toLocaleString('ms-MY', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
